import { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { queryKeys } from '../../services/query/queryKeys';
import { teamPreferencesService, type TeamPreferences } from '../../services/teamPreferences.service';
import {
  DEFAULT_BRAND_PRIMARY_COLOUR,
  getBrandHexColourError,
  normalizeBrandHexColour,
} from '../../utils/brandColour';

interface BrandPrimaryColourFieldProps {
  colour?: string;
  canEdit?: boolean;
}

export default function BrandPrimaryColourField({ colour, canEdit = false }: BrandPrimaryColourFieldProps) {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();
  const savedColour = normalizeBrandHexColour(colour) || DEFAULT_BRAND_PRIMARY_COLOUR;
  const [draftColour, setDraftColour] = useState(savedColour);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const normalizedDraft = normalizeBrandHexColour(draftColour);
  const previewColour = normalizedDraft || savedColour;
  const validationError = getBrandHexColourError(draftColour);
  const isDirty = normalizedDraft !== null && normalizedDraft !== savedColour;
  const isDefaultColour = !normalizeBrandHexColour(colour);

  useEffect(() => {
    setDraftColour(savedColour);
  }, [savedColour]);

  const updateDraft = (value: string) => {
    setDraftColour(value.toUpperCase());
    setHasInteracted(true);
    setError(null);
    setSuccess(null);
  };

  const handleSave = async () => {
    if (!currentUser?.uid) return;

    const nextColour = normalizeBrandHexColour(draftColour);
    if (!nextColour) {
      setHasInteracted(true);
      setError(getBrandHexColourError(draftColour));
      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const saved = await teamPreferencesService.updateBrandPrimaryColour(nextColour);
      const teamPreferencesKey = queryKeys.preferences.team(currentUser.uid);
      queryClient.setQueryData<TeamPreferences>(teamPreferencesKey, (current) =>
        current ? { ...current, brand_primary_colour: saved } : current,
      );
      setDraftColour(saved);
      setHasInteracted(false);
      setSuccess('Primary brand colour updated for everyone on this team.');
      await queryClient.invalidateQueries({ queryKey: teamPreferencesKey });
    } catch (saveError: any) {
      setError(saveError?.message || 'The primary brand colour could not be saved.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="overflow-hidden rounded-[28px] border border-white/8 bg-[linear-gradient(145deg,rgba(255,255,255,0.035),rgba(82,96,255,0.075)_65%,rgba(255,255,255,0.018))]">
      <div className="grid gap-6 p-5 md:grid-cols-[180px_minmax(0,1fr)] md:p-6">
        <div className="relative flex min-h-44 items-center justify-center overflow-hidden rounded-[22px] border border-white/10 bg-black/35 p-5">
          <div
            aria-hidden="true"
            className="absolute inset-0 opacity-35 transition-colors duration-300"
            style={{ background: `radial-gradient(circle at 50% 48%, ${previewColour}, transparent 68%)` }}
          />
          <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-black/35 px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.14em] text-white/60">
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: previewColour }} />
            {isDefaultColour ? 'Brainiacs default' : 'Team colour'}
          </span>
          <div
            className="relative mt-5 flex h-24 w-24 items-center justify-center rounded-[26px] border border-white/20 text-3xl font-black tracking-[-0.06em] text-white shadow-[0_18px_45px_rgba(0,0,0,0.32),inset_0_1px_0_rgba(255,255,255,0.24)] transition-colors duration-300"
            style={{ backgroundColor: previewColour }}
          >
            Aa
          </div>
        </div>

        <div className="min-w-0">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[#7580ff]/25 bg-[#5260FF]/15 text-indigo-100">
              <Icon icon="bx:palette" className="h-4.5 w-4.5" />
            </span>
            <div>
              <h3 className="text-lg font-black tracking-tight text-white">Primary brand colour</h3>
              <p className="mt-1 text-sm leading-6 text-gray-400">
                Choose a colour or enter its hex code. It will be shared through this Team’s branding preferences.
              </p>
            </div>
          </div>

          {!canEdit ? (
            <div className="mt-4 flex items-center justify-between gap-4 rounded-2xl border border-amber-300/18 bg-amber-300/[0.065] px-4 py-3.5">
              <div className="flex min-w-0 items-start gap-3 text-sm leading-5 text-amber-50/80">
                <Icon icon="bx:lock-alt" className="mt-0.5 h-4.5 w-4.5 shrink-0 text-amber-200" />
                <span><strong className="font-black text-amber-50">View only.</strong> Only Partners can edit</span>
              </div>
              <span className="shrink-0 font-mono text-xs font-black tracking-[0.08em] text-white">{savedColour}</span>
            </div>
          ) : (
          <div className="mt-4 rounded-2xl border border-white/10 bg-black/20 p-4">
            <div className="grid gap-3 sm:grid-cols-[160px_minmax(0,1fr)] sm:items-start">
              <div className="min-w-0">
                <span className="mb-1.5 block text-[10px] font-black uppercase tracking-[0.14em] text-white/45">
                  Colour picker
                </span>
                <label className="group relative flex h-11 w-full cursor-pointer items-center gap-3 rounded-xl border border-white/12 bg-white/[0.055] px-3 transition-colors hover:bg-white/[0.085] focus-within:border-[#7d87ff]/55 focus-within:ring-2 focus-within:ring-[#5260FF]/15">
                  <span
                    className="h-6 w-6 rounded-lg border border-white/20 shadow-[inset_0_1px_0_rgba(255,255,255,0.25)]"
                    style={{ backgroundColor: previewColour }}
                  />
                  <span className="text-xs font-black text-white">Pick colour</span>
                  <input
                    type="color"
                    value={previewColour}
                    aria-label="Choose primary brand colour"
                    className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                    onChange={(event) => updateDraft(event.currentTarget.value)}
                  />
                </label>
              </div>

              <div className="min-w-0 flex-1">
                <label htmlFor="brand-primary-colour" className="mb-1.5 block text-[10px] font-black uppercase tracking-[0.14em] text-white/45">
                  Hexadecimal code
                </label>
                <div className={`flex h-11 items-center rounded-xl border bg-black/25 transition-colors focus-within:ring-2 ${
                  hasInteracted && validationError
                    ? 'border-rose-400/45 focus-within:ring-rose-400/15'
                    : 'border-white/12 focus-within:border-[#7d87ff]/55 focus-within:ring-[#5260FF]/15'
                }`}>
                  <input
                    id="brand-primary-colour"
                    type="text"
                    value={draftColour}
                    inputMode="text"
                    autoComplete="off"
                    spellCheck={false}
                    maxLength={7}
                    placeholder={DEFAULT_BRAND_PRIMARY_COLOUR}
                    aria-invalid={hasInteracted && Boolean(validationError)}
                    aria-describedby={hasInteracted && validationError ? 'brand-primary-colour-error' : 'brand-primary-colour-help'}
                    className="h-full min-w-0 flex-1 bg-transparent px-3 font-mono text-sm font-bold tracking-[0.08em] text-white outline-none placeholder:text-white/25"
                    onChange={(event) => updateDraft(event.currentTarget.value)}
                    onBlur={() => {
                      if (normalizedDraft) setDraftColour(normalizedDraft);
                    }}
                  />
                  <span className="mr-3 font-mono text-[10px] font-black uppercase tracking-[0.12em] text-white/30">HEX</span>
                </div>
                <p id="brand-primary-colour-help" className="mt-1.5 text-[11px] leading-4 text-white/35">
                  Six digits, with or without the # symbol.
                </p>
              </div>
            </div>
          </div>
          )}

          {hasInteracted && validationError ? (
            <div id="brand-primary-colour-error" role="alert" className="mt-3 flex items-start gap-2 rounded-xl border border-rose-400/25 bg-rose-400/10 px-3 py-2.5 text-xs font-semibold leading-5 text-rose-100">
              <Icon icon="bx:error-circle" className="mt-0.5 h-4 w-4 shrink-0" />{validationError}
            </div>
          ) : error ? (
            <div role="alert" className="mt-3 flex items-start gap-2 rounded-xl border border-rose-400/25 bg-rose-400/10 px-3 py-2.5 text-xs font-semibold leading-5 text-rose-100">
              <Icon icon="bx:error-circle" className="mt-0.5 h-4 w-4 shrink-0" />{error}
            </div>
          ) : null}
          {success ? (
            <div role="status" className="mt-3 flex items-start gap-2 rounded-xl border border-emerald-400/25 bg-emerald-400/10 px-3 py-2.5 text-xs font-semibold leading-5 text-emerald-100">
              <Icon icon="bx:check-circle" className="mt-0.5 h-4 w-4 shrink-0" />{success}
            </div>
          ) : null}

          {canEdit ? (
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving || !isDirty || Boolean(validationError)}
              className="inline-flex items-center gap-2 rounded-xl border border-[#7580ff]/35 bg-[#5260FF] px-4 py-2 text-xs font-black text-white shadow-[0_12px_26px_rgba(82,96,255,0.25)] transition-colors hover:bg-[#4553f1] disabled:cursor-not-allowed disabled:opacity-45"
            >
              <Icon icon={isSaving ? 'bx:loader-circle' : 'bx:save'} className={`h-4 w-4 ${isSaving ? 'animate-spin' : ''}`} />
              {isSaving ? 'Saving to Appwrite…' : 'Save brand colour'}
            </button>
          </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
